export interface Chiefdom {
  id: string;
  name: string;
  alternateNames?: string[];
  district: string;
  province: string;
  historicalTerritory: string;
  clanLineage: string;
  naturalBoundaries: string;
  historicalNotes: string;
  era: string;
  coordinates: { x: number; y: number }; // relative % on SVG map
}

export interface Province {
  id: string;
  name: string;
  color: string;
  districts: string[];
  svgPath: string;
}

export const PROVINCES: Province[] = [
  {
    id: 'mashonaland-west',
    name: 'Mashonaland West',
    color: '#8B4513',
    districts: ['Hurungwe', 'Kariba', 'Makonde', 'Zvimba', 'Chegutu', 'Sanyati', 'Kadoma'],
    svgPath: 'M 60 30 L 200 25 L 220 80 L 200 140 L 140 160 L 80 150 L 50 100 Z',
  },
  {
    id: 'mashonaland-central',
    name: 'Mashonaland Central',
    color: '#556B2F',
    districts: ['Bindura', 'Guruve', 'Mazowe', 'Mount Darwin', 'Rushinga', 'Shamva', 'Muzarabani'],
    svgPath: 'M 200 25 L 340 20 L 360 90 L 320 150 L 220 160 L 200 140 L 220 80 Z',
  },
  {
    id: 'mashonaland-east',
    name: 'Mashonaland East',
    color: '#2E8B57',
    districts: ['Chikomba', 'Goromonzi', 'Marondera', 'Mudzi', 'Murehwa', 'Mutoko', 'Seke', 'UMP', 'Hwedza'],
    svgPath: 'M 340 20 L 460 30 L 470 130 L 400 200 L 320 190 L 320 150 L 360 90 Z',
  },
  {
    id: 'manicaland',
    name: 'Manicaland',
    color: '#8B0000',
    districts: ['Buhera', 'Chimanimani', 'Chipinge', 'Makoni', 'Mutare', 'Mutasa', 'Nyanga'],
    svgPath: 'M 460 30 L 560 60 L 570 200 L 480 280 L 400 260 L 400 200 L 470 130 Z',
  },
  {
    id: 'masvingo',
    name: 'Masvingo',
    color: '#DAA520',
    districts: ['Bikita', 'Chiredzi', 'Chivi', 'Gutu', 'Masvingo', 'Mwenezi', 'Zaka'],
    svgPath: 'M 320 190 L 480 280 L 470 400 L 360 430 L 240 400 L 220 300 L 280 240 Z',
  },
  {
    id: 'midlands',
    name: 'Midlands',
    color: '#4682B4',
    districts: ['Chirumhanzu', 'Gokwe North', 'Gokwe South', 'Gweru', 'Kwekwe', 'Mberengwa', 'Shurugwi', 'Zvishavane'],
    svgPath: 'M 140 160 L 320 150 L 320 190 L 280 240 L 220 300 L 160 280 L 100 220 L 100 180 Z',
  },
  {
    id: 'matabeleland-north',
    name: 'Matabeleland North',
    color: '#800080',
    districts: ['Binga', 'Bubi', 'Hwange', 'Lupane', 'Nkayi', 'Tsholotsho', 'Umguza'],
    svgPath: 'M 50 100 L 140 160 L 100 180 L 100 280 L 60 300 L 20 240 L 20 140 Z',
  },
  {
    id: 'matabeleland-south',
    name: 'Matabeleland South',
    color: '#B8860B',
    districts: ['Beitbridge', 'Bulilima', 'Gwanda', 'Insiza', 'Mangwe', 'Matobo', 'Umzingwane'],
    svgPath: 'M 60 300 L 160 280 L 220 300 L 240 400 L 200 460 L 100 450 L 50 380 Z',
  },
  {
    id: 'harare',
    name: 'Harare Metropolitan',
    color: '#CC0000',
    districts: ['Harare', 'Chitungwiza', 'Epworth'],
    svgPath: 'M 290 145 L 320 145 L 320 175 L 290 175 Z',
  },
  {
    id: 'bulawayo',
    name: 'Bulawayo Metropolitan',
    color: '#CC5500',
    districts: ['Bulawayo'],
    svgPath: 'M 110 295 L 140 295 L 140 320 L 110 320 Z',
  },
];

export const CHIEFDOMS: Chiefdom[] = [
  // ── MASHONALAND WEST ──────────────────────────────────────────────────────
  {
    id: 'chief-zvimba',
    name: 'Chief Zvimba',
    district: 'Zvimba',
    province: 'Mashonaland West',
    historicalTerritory: 'Zvimba Plateau',
    clanLineage: 'Shava (Eland) totem — Rozvi lineage',
    naturalBoundaries: 'Manyame River to the east; Hunyani escarpment to the south',
    historicalNotes: 'One of the oldest chieftainships in Mashonaland West, with roots in the pre-Rozvi era. The Zvimba chiefdom was central to early missionary contact in the region. Chief Zvimba (Stanley Mhondoro Wurayayi) was installed on 20 July 2009.',
    era: 'Pre-colonial to present',
    coordinates: { x: 22, y: 32 },
  },
  {
    id: 'chief-chirau',
    name: 'Chief Chirau',
    district: 'Zvimba',
    province: 'Mashonaland West',
    historicalTerritory: 'Chirau communal lands, Zvimba District',
    clanLineage: 'Shava (Eland) totem — Zezuru lineage',
    naturalBoundaries: 'Manyame River corridor; bounded by granite kopjes',
    historicalNotes: 'Chief Chirau was a prominent traditional leader during the Zimbabwe independence negotiations. The chieftainship has been held by the Dzvakakuyambwa family. Chief Chirau (Tinashe Dzvakakuyambwa) was installed as Acting Chief on 15 August 2011.',
    era: 'Pre-colonial to present',
    coordinates: { x: 24, y: 35 },
  },
  {
    id: 'chief-hurungwe',
    name: 'Chief Hurungwe',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Zambezi Valley lowlands and Hurungwe highlands',
    clanLineage: 'Tembo (Elephant) totem — Korekore lineage',
    naturalBoundaries: 'Zambezi River to the north; Manyame River to the east',
    historicalNotes: 'The Hurungwe chiefdom spans the transition zone between the Zambezi Valley and the highveld plateau, historically a trade corridor between the interior and the Zambezi. The area is part of the ancient Monomutapa Kingdom sphere.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 14, y: 20 },
  },
  {
    id: 'chief-makonde',
    name: 'Chief Nemakonde',
    alternateNames: ['Makonde area chief'],
    district: 'Makonde',
    province: 'Mashonaland West',
    historicalTerritory: 'Makonde Hills and surrounding lowlands',
    clanLineage: 'Shumba (Lion) totem — Zezuru lineage',
    naturalBoundaries: 'Defined by the Makonde Hills range and seasonal rivers',
    historicalNotes: 'Chief Nemakonde (W. Jimani Mhende) was installed on 9 January 1985. The Makonde chiefdom occupies the rocky terrain of the Makonde Hills, historically a defensive stronghold. The area borders Mashonaland Central to the north.',
    era: 'Pre-colonial to present',
    coordinates: { x: 20, y: 38 },
  },
  {
    id: 'chief-kazangarare',
    name: 'Chief Kazangarare',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Ruvanga area, Hurungwe District',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Korekore lineage',
    naturalBoundaries: 'Dete Valley to the west; Sanyati River to the south',
    historicalNotes: 'Descendants of Mutota, originally from Guruuswa. Chief Kazangarare (Obiri Peter Maendaenda) was installed on 31 August 1983. The chiefdom is related to Chiefs Chisunga, Chitsungo and Dendera, all sharing the same totem and chidau.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 16, y: 28 },
  },
  {
    id: 'chief-dendera',
    name: 'Chief Dendera',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Magunje area, Hurungwe District',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Korekore (VaGova) lineage',
    naturalBoundaries: 'Tengwe River to the south; Murerezhi River to the east',
    historicalNotes: 'The Dendera name derives from the ground hornbill bird. Descendants of Mutota from the Dande area of Chipuriro. Chief Dendera (Noah Kerechani Dendera) was installed on 20 December 1984. The chiefdom includes Magunje growth point.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 18, y: 32 },
  },
  {
    id: 'chief-dandawa',
    name: 'Chief Dandawa',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Rengwe area, Hurungwe District',
    clanLineage: 'Tembo Mazvimbakupa (Elephant) totem — VaGova (Korekore) lineage',
    naturalBoundaries: 'Kanyati River to the north; Mudzimu area to the south',
    historicalNotes: 'Originally lived along the Zambezi Valley near Mana Pools. The Dandawa people were moved to Rengwe in 1958 when Lake Kariba was created. Chief Dandawa (F. Manyepa) was installed on 10 March 1998.',
    era: 'Pre-colonial to present',
    coordinates: { x: 12, y: 24 },
  },
  {
    id: 'chief-nyamhunga',
    name: 'Chief Nyamhunga',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'North-western Hurungwe District',
    clanLineage: 'Gwai (Zambu/Munawa) totem — Korekore lineage',
    naturalBoundaries: 'Kamazi River to the north; Nyadara River to the south',
    historicalNotes: 'Originally from the Zambezi Valley, displaced in 1957 for the Kariba Dam. Believed to be an offshoot of the Gora dynasty from Goromonzi. Chief Nyamhunga (Boniface Chinehasha) was installed on 23 December 2005.',
    era: 'Pre-colonial to present',
    coordinates: { x: 10, y: 22 },
  },
  {
    id: 'chief-nematombo',
    name: 'Chief Nematombo',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Western Hurungwe District',
    clanLineage: 'Shumba Nechinanga (Lion) totem — VaBudya/Korekore lineage',
    naturalBoundaries: 'Tengwe River to the south; Lake Kariba shores to the north',
    historicalNotes: 'Ancestors migrated from Guruuswa and Mutoko, claiming descent from Nehoreka. Chief Nematombo (Ben Shiridzinodya) was installed as Acting Chief on 7 December 2006. The chiefdom stretches from Tengwe River to Lake Kariba shores.',
    era: 'Pre-colonial to present',
    coordinates: { x: 14, y: 26 },
  },
  {
    id: 'chief-mujinga',
    name: 'Chief Mujinga',
    district: 'Hurungwe',
    province: 'Mashonaland West',
    historicalTerritory: 'Piriwiri area, Hurungwe District',
    clanLineage: 'Tembo Mazvimbakupa (Elephant) totem — VaMbire (Korekore) lineage',
    naturalBoundaries: 'Tengwe River to the south; Sanyati River to the east; Piriwiri River to the north',
    historicalNotes: 'Related to Chief Chihota of Marondera. Chief Mujinga (Mutenhe Mudanhairwa) was installed on 8 October 2007. The tribe is said to have originated from Guruuswa. Early ancestors were elephant hunters.',
    era: 'Pre-colonial to present',
    coordinates: { x: 16, y: 30 },
  },
  {
    id: 'chief-mashayamombe',
    name: 'Chief Mashayamombe',
    district: 'Chegutu',
    province: 'Mashonaland West',
    historicalTerritory: 'Chegutu District communal lands',
    clanLineage: 'Shava (Eland) totem — Zezuru lineage',
    naturalBoundaries: 'Manyame River to the east; Sanyati River to the west',
    historicalNotes: 'Chief Mashayamombe was a key leader during the First Chimurenga (1896–97). Chief Mashayamombe (Ignatius Stephen Chiketa) was installed on 28 March 2007. The chiefdom has strong historical ties to the Rozvi state.',
    era: 'Pre-colonial to present',
    coordinates: { x: 26, y: 40 },
  },
  {
    id: 'chief-mola',
    name: 'Chief Mola',
    district: 'Kariba',
    province: 'Mashonaland West',
    historicalTerritory: 'Kariba District (formerly Zambezi Valley)',
    clanLineage: 'Nyoni Mushanga Muyuni totem — BaTonga lineage',
    naturalBoundaries: 'Bumi River to the east; Sengwa River to the west; Lake Kariba to the north',
    historicalNotes: 'The Tonga people of Mola were displaced from the Zambezi Valley when Lake Kariba was created in 1958. Chief Mola (Champion Rare) was installed on 29 May 1997. Unlike Shona patriarchy, Mola uses matrilineal succession.',
    era: 'Pre-colonial to present',
    coordinates: { x: 20, y: 22 },
  },
  {
    id: 'chief-nebiri',
    name: 'Chief Nebiri',
    district: 'Kariba',
    province: 'Mashonaland West',
    historicalTerritory: 'Kariba District — Chifudze/Hwadze area',
    clanLineage: 'Shoko-Ncube (Chirongo) totem — VaShangwe lineage',
    naturalBoundaries: 'Ndepa Mountain range to the west; Bumi River to the east',
    historicalNotes: 'The name Nebiri derives from the valley name, never a personal name. Granted chieftainship by Chief Chireya of Gokwe. Chief Nebiri (Wilson Nebiri) was installed on 6 July 2006. The chiefdom holds important rain-making ceremonies.',
    era: 'Pre-colonial to present',
    coordinates: { x: 22, y: 24 },
  },
  {
    id: 'chief-musampakaruma',
    name: 'Chief Musampakaruma',
    alternateNames: ['Msampakaruma'],
    district: 'Kariba',
    province: 'Mashonaland West',
    historicalTerritory: 'Kariba District — between Sanyati and Bumi rivers',
    clanLineage: 'Shoko Wafawanaka (Monkey) totem — BaGova (Shona) lineage',
    naturalBoundaries: 'Sanyati River to the east; Bumi River to the west',
    historicalNotes: 'Displaced by Kariba Dam in 1956. Chief Musampakaruma (J. Chabwededza) was installed on 28 October 1981. Among the earliest Shona people to settle along the Zambezi. The tribe was conquered by the Ndebele and paid taxes in tobacco.',
    era: 'Pre-colonial to present',
    coordinates: { x: 18, y: 26 },
  },

  // ── MASHONALAND CENTRAL ───────────────────────────────────────────────────
  {
    id: 'chief-guruve',
    name: 'Chief Guruve',
    district: 'Guruve',
    province: 'Mashonaland Central',
    historicalTerritory: 'Zambezi escarpment and Guruve plateau',
    clanLineage: 'Tembo (Elephant) totem — Korekore lineage',
    naturalBoundaries: 'Zambezi escarpment to the north; Mazowe River valley to the south',
    historicalNotes: 'The Guruve chiefdom sits at the edge of the Zambezi escarpment, historically part of the Korekore sphere of influence that extended into the Zambezi Valley under the Monomutapa Kingdom.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 42, y: 18 },
  },
  {
    id: 'chief-bepura',
    name: 'Chief Bepura',
    district: 'Guruve',
    province: 'Mashonaland Central',
    historicalTerritory: 'Guruve District communal lands',
    clanLineage: 'Korekore lineage',
    naturalBoundaries: 'Mazowe River valley; Zambezi escarpment',
    historicalNotes: 'Chief Bepura (Kenneth Silas Bepura) was installed on 20 March 2006. The chiefdom is one of several in Guruve District with deep Korekore heritage and connections to the Monomutapa Kingdom.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 44, y: 16 },
  },
  {
    id: 'chief-chipuriro',
    name: 'Chief Chipuriro',
    district: 'Guruve',
    province: 'Mashonaland Central',
    historicalTerritory: 'Chipuriro/Guruve area',
    clanLineage: 'Korekore lineage',
    naturalBoundaries: 'Mazowe River; Ruya River',
    historicalNotes: 'Chief Chipuriro (Clever Mashiki) was installed in 2016. The Chipuriro area is mentioned in the Monomutapa Kingdom records as a significant territory. The ancestral spirit Mukombwe is revered in this area.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 40, y: 20 },
  },
  {
    id: 'chief-mazowe',
    name: 'Chief Negomo',
    alternateNames: ['Mazowe area chief'],
    district: 'Mazowe',
    province: 'Mashonaland Central',
    historicalTerritory: 'Mazowe Valley',
    clanLineage: 'Moyo Ndizvo (Heart) totem — Zezuru lineage',
    naturalBoundaries: 'Mazowe River defines the valley floor; granite hills to east and west',
    historicalNotes: 'The Negomo chieftainship began with Chiodzamamera (also known as Mugumo). The Mazowe Valley was a major gold-producing region during the Zimbabwe Plateau civilizations. Chief Negomo (Lucius Chibvongodze) was installed as Acting Chief on 7 June 2010.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 50, y: 28 },
  },
  {
    id: 'chief-makope',
    name: 'Chief Makope',
    district: 'Mazowe',
    province: 'Mashonaland Central',
    historicalTerritory: 'Mazowe District communal lands',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Zezuru lineage',
    naturalBoundaries: 'Mazowe River; Ruya River to the north',
    historicalNotes: 'Chief Makope (M.J. Kuvataiya) was installed on 12 December 1984. The chiefdom is in the Mazowe District and has historical connections to the Chiweshe area. The Mazowe Valley was central to early Portuguese and British contact with Zimbabwe.',
    era: 'Pre-colonial to present',
    coordinates: { x: 52, y: 26 },
  },
  {
    id: 'chief-chiweshe',
    name: 'Chief Chiweshe',
    district: 'Mazowe',
    province: 'Mashonaland Central',
    historicalTerritory: 'Chiweshe Tribal Trust Lands',
    clanLineage: 'Shava Mufakose (Eland) totem — Zezuru lineage',
    naturalBoundaries: 'Mazowe River to the west; Ruya River to the north',
    historicalNotes: 'The Chiweshe area was a significant site of the First Chimurenga (1896–97). Chief Chiweshe (Joseph Chigariro) was installed in October 2005. Three sons of Nyashanu — Chiweshe, Hwata and Gutsa — migrated northwards from Buhera to establish this chieftainship.',
    era: 'Pre-colonial to present',
    coordinates: { x: 55, y: 22 },
  },
  {
    id: 'chief-dotito',
    name: 'Chief Dotito',
    district: 'Mount Darwin',
    province: 'Mashonaland Central',
    historicalTerritory: 'Kandeya Tribal Trust Land, Mount Darwin',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Nyombwe (Korekore) lineage',
    naturalBoundaries: 'Fusiri stream boundary with Nembire; Ruya River to the north',
    historicalNotes: 'Chief Dotito was appointed in 1938. The chieftainship covers the matunhu of Dotito, Matope, Kandeya, Nowedza, Nembire, Pachanza and Chitsato. Chief Dotito (Sirako Manyika) was installed on 2 March 1999.',
    era: 'Pre-colonial to present',
    coordinates: { x: 48, y: 18 },
  },
  {
    id: 'chief-nembire',
    name: 'Chief Nembire',
    district: 'Mount Darwin',
    province: 'Mashonaland Central',
    historicalTerritory: 'Mbire dunhu, Mount Darwin District',
    clanLineage: 'Soko Bvudzijena (Monkey) totem — VaMbire lineage',
    naturalBoundaries: 'Fusiri stream boundary with Dotito',
    historicalNotes: 'The VaMbire people migrated from the Wedza area and are affiliated to the Soswe chieftainship. Allocated their dunhu by Mukombwe, the ruling Monomutapa. Chief Nembire (Clemence Nembire) was installed on 31 August 2004.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 50, y: 16 },
  },
  {
    id: 'chief-kandeya',
    name: 'Chief Kandeya',
    district: 'Mount Darwin',
    province: 'Mashonaland Central',
    historicalTerritory: 'Kandeya area, Mount Darwin District',
    clanLineage: 'Tembo Mazvimbakupa (Elephant) totem — Korekore lineage',
    naturalBoundaries: 'Ruya River; Mazowe River',
    historicalNotes: 'Chief Kandeya (Zabron Mawande) was installed on 15 August 1994. The Kandeya chieftainship is one of six in Mount Darwin District. The area has strong Korekore heritage with connections to the Monomutapa Kingdom.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 52, y: 18 },
  },
  {
    id: 'chief-musana',
    name: 'Chief Musana',
    district: 'Bindura',
    province: 'Mashonaland Central',
    historicalTerritory: 'Bindura District communal lands',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Zezuru lineage',
    naturalBoundaries: 'Mazowe River; Bindura hills',
    historicalNotes: 'Chief Musana (Joel Nyarumwe) was installed on 20 February 2006. The Musana chieftainship is in Bindura District and has historical connections to the Mutapa state. The area is known for its gold deposits mined since pre-colonial times.',
    era: 'Pre-colonial to present',
    coordinates: { x: 54, y: 24 },
  },
  {
    id: 'chief-masembura',
    name: 'Chief Masembura',
    district: 'Bindura',
    province: 'Mashonaland Central',
    historicalTerritory: 'Bindura District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Mazowe River valley; Bindura hills',
    historicalNotes: 'Chief Masembura (Amon Nhapi) was installed on 20 February 2006. The chieftainship is in Bindura District, Mashonaland Central. The area has rich agricultural land and gold deposits.',
    era: 'Pre-colonial to present',
    coordinates: { x: 56, y: 26 },
  },
  {
    id: 'chief-makuni',
    name: 'Chief Makuni',
    district: 'Rushinga',
    province: 'Mashonaland Central',
    historicalTerritory: 'Rushinga District',
    clanLineage: 'Korekore lineage — Monomutapa descent',
    naturalBoundaries: 'Ruya River; Rushinga hills',
    historicalNotes: 'Dombo Makuni was brother to Nyahuwi and Koswa, daughters of Monomotapa Mukombwe. He was appointed to rule at Sirisiri on the Ruya River. Chief Makuni (Nicholas Muzika) was installed on 4 May 1989.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 58, y: 20 },
  },
  {
    id: 'chief-nyakusengwa',
    name: 'Chief Nyakusengwa',
    alternateNames: ['Mukosa'],
    district: 'Rushinga',
    province: 'Mashonaland Central',
    historicalTerritory: 'Rushinga District',
    clanLineage: 'Nzou Samanyanga (Elephant) totem — Korekore-Tavara lineage',
    naturalBoundaries: 'Ruya River; Rushinga hills',
    historicalNotes: 'Installed by the Mhondoro Koswa, the female ancestor of the tribe. The chieftaincy was previously called Mukosa. Chief Nyakusengwa (Moffat Chimunya) was installed as Acting Chief on 15 May 2011. Rain-making and harvest ceremonies are conducted.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 60, y: 18 },
  },
  {
    id: 'chief-rusambo',
    name: 'Chief Rusambo',
    district: 'Rushinga',
    province: 'Mashonaland Central',
    historicalTerritory: 'Chimanda Tribal Trust Land, Rushinga',
    clanLineage: 'Soko (Monkey) totem — Rozvi lineage',
    naturalBoundaries: 'Ruya River; Rushinga hills',
    historicalNotes: 'The Rusambo community migrated from Shawasha as a result of Ruwambwe\'s exploits and was allocated land by Mutota. Chief Rusambo (Gladmore Kangora) was installed as Acting Chief on 19 April 2000. The area includes Mahutwe, Gwangwava, Katevera and Chimanda.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 62, y: 20 },
  },
  {
    id: 'chief-nyamaropa',
    name: 'Chief Nyamaropa',
    district: 'Shamva',
    province: 'Mashonaland Central',
    historicalTerritory: 'Shamva District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Mazowe River; Shamva hills',
    historicalNotes: 'Chief Nyamaropa (Enock Ndivangi) was installed on 31 August 2004. The Shamva District is known for its gold deposits and has been inhabited since the Great Zimbabwe era. The chieftainship has strong connections to the Mazowe Valley civilizations.',
    era: 'Pre-colonial to present',
    coordinates: { x: 56, y: 28 },
  },
  {
    id: 'chief-bushu',
    name: 'Chief Bushu',
    district: 'Shamva',
    province: 'Mashonaland Central',
    historicalTerritory: 'Shamva District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Mazowe River; Shamva hills',
    historicalNotes: 'Chief Bushu (Show Bushu) was installed on 22 March 1999. The chieftainship is in Shamva District, known for its gold mining history. The area has connections to the Mazowe Valley trade routes of the pre-colonial era.',
    era: 'Pre-colonial to present',
    coordinates: { x: 58, y: 26 },
  },

  // ── MASHONALAND EAST ──────────────────────────────────────────────────────
  {
    id: 'chief-mutoko',
    name: 'Chief Mutoko',
    alternateNames: ['Mtoko'],
    district: 'Mutoko',
    province: 'Mashonaland East',
    historicalTerritory: 'Mutoko highlands and Nyamapanda corridor',
    clanLineage: 'Tembo (Elephant) totem — Budya lineage',
    naturalBoundaries: 'Ruya River to the north; Nyadiri River to the south',
    historicalNotes: 'The Mutoko chiefdom was historically a gateway between the Zimbabwe Plateau and the Mozambique coast, controlling important trade routes. Chief Mutoko (Michael Gurupira) was installed as Acting Chief on 9 September 2011.',
    era: 'Kingdom of Mutapa era to present',
    coordinates: { x: 68, y: 22 },
  },
  {
    id: 'chief-mudzi',
    name: 'Chief Mudzi',
    district: 'Mudzi',
    province: 'Mashonaland East',
    historicalTerritory: 'Mudzi River basin',
    clanLineage: 'Shumba (Lion) totem — Korekore lineage',
    naturalBoundaries: 'Mudzi River defines the territory; Mozambique border to the east',
    historicalNotes: 'The Mudzi chiefdom occupies the northeastern frontier of Zimbabwe, with historical ties to Mozambican Shona communities across the border. Chief Nyakuchena (Pera Bvunzawabaya) was installed on 21 November 1981.',
    era: 'Pre-colonial to present',
    coordinates: { x: 72, y: 18 },
  },
  {
    id: 'chief-marondera',
    name: 'Chief Chihota',
    alternateNames: ['Marondera area chief'],
    district: 'Marondera',
    province: 'Mashonaland East',
    historicalTerritory: 'Marondera plateau',
    clanLineage: 'Nzou (Elephant) totem — Zezuru lineage',
    naturalBoundaries: 'Marondera River; surrounding granite kopjes',
    historicalNotes: 'Chief Marondera was a prominent leader during the early colonial period. The town of Marondera (formerly Marandellas) was named after him. Chief Chihota (Frederick Mapfumo Chigodora) was installed on 3 December 2007.',
    era: 'Pre-colonial to present',
    coordinates: { x: 62, y: 32 },
  },
  {
    id: 'chief-mangwende',
    name: 'Chief Mangwende',
    district: 'Murehwa',
    province: 'Mashonaland East',
    historicalTerritory: 'Mangwende Tribal Trust Land, Murehwa',
    clanLineage: 'VaNowe tribe — Zezuru lineage',
    naturalBoundaries: 'Nyadiri River; Murehwa hills',
    historicalNotes: 'Chief Mangwende (Tafirenyika John Chibanda) was installed on 1 March 1969. The Mangwende Tribal Trust Land is one of four in Murehwa District. The chieftainship has headmen Zihute, Mushaninga and Mukarakate under it.',
    era: 'Pre-colonial to present',
    coordinates: { x: 64, y: 28 },
  },
  {
    id: 'chief-chinamhora',
    name: 'Chief Chinamhora',
    alternateNames: ['Chinamano'],
    district: 'Goromonzi',
    province: 'Mashonaland East',
    historicalTerritory: 'Goromonzi District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Manyame River; Goromonzi hills',
    historicalNotes: 'Chief Chinamhora (Simon Chidziva) was installed on 15 June 2000. The Goromonzi District is close to Harare and has been an important area for Zezuru chieftainships. The area has connections to the Rozvi state.',
    era: 'Pre-colonial to present',
    coordinates: { x: 60, y: 30 },
  },
  {
    id: 'chief-chikwaka',
    name: 'Chief Chikwaka',
    district: 'Goromonzi',
    province: 'Mashonaland East',
    historicalTerritory: 'Goromonzi District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Manyame River; Goromonzi hills',
    historicalNotes: 'Chief Chikwaka (Kimpton Chigeramasambe) was installed as Acting Chief on 8 December 2010. The chieftainship is in Goromonzi District, close to Harare Metropolitan Province. The area has strong Zezuru cultural traditions.',
    era: 'Pre-colonial to present',
    coordinates: { x: 62, y: 28 },
  },
  {
    id: 'chief-chikomba',
    name: 'Chief Mutekedza',
    alternateNames: ['Chikomba area chief'],
    district: 'Chikomba',
    province: 'Mashonaland East',
    historicalTerritory: 'Chikomba District',
    clanLineage: 'Shava Museyamwa (Eland) totem — Karanga/Zezuru lineage',
    naturalBoundaries: 'Mupfure River; Chikomba hills',
    historicalNotes: 'Chief Mutekedza (Andrew Zhakata) was installed on 29 June 1995. The Chikomba District has several chieftainships including Mutekedza, Chivese and Nyoka. The area is at the boundary between Zezuru and Karanga cultural spheres.',
    era: 'Pre-colonial to present',
    coordinates: { x: 58, y: 34 },
  },
  {
    id: 'chief-hwedza',
    name: 'Chief Nyahuye wa Svosve',
    alternateNames: ['Hwedza area chief'],
    district: 'Hwedza',
    province: 'Mashonaland East',
    historicalTerritory: 'Hwedza District',
    clanLineage: 'Zezuru lineage',
    naturalBoundaries: 'Mupfure River; Hwedza hills',
    historicalNotes: 'Chief Nyahuye wa Svosve (Weston Zvenyika) was installed on 10 July 2008. The Hwedza District is known for its granite hills and has been inhabited since the Iron Age. The area has connections to the Great Zimbabwe civilization.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 60, y: 36 },
  },

  // ── MANICALAND ────────────────────────────────────────────────────────────
  {
    id: 'chief-mutasa',
    name: 'Chief Mutasa',
    district: 'Mutasa',
    province: 'Manicaland',
    historicalTerritory: 'Honde Valley and Nyanga highlands',
    clanLineage: 'Shumba (Lion) totem — Manyika lineage',
    naturalBoundaries: 'Pungwe River to the south; Mozambique border (Eastern Highlands) to the east',
    historicalNotes: 'The Mutasa chiefdom was one of the most powerful in Manicaland, controlling the fertile Honde Valley. Chief Mutasa negotiated with both the Portuguese and British in the late 19th century. Chief Mutasa (Misheck Pasi Mutasa) was installed on 25 June 2004.',
    era: 'Pre-colonial to present',
    coordinates: { x: 80, y: 28 },
  },
  {
    id: 'chief-makoni',
    name: 'Chief Makoni',
    district: 'Makoni',
    province: 'Manicaland',
    historicalTerritory: 'Makoni highlands',
    clanLineage: 'Soko (Monkey) totem — Manyika lineage',
    naturalBoundaries: 'Rusape River valley to the west; Nyanga highlands to the north',
    historicalNotes: 'Chief Makoni led one of the most significant armed resistances during the First Chimurenga. He was captured and executed by the British in 1896. Chief Makoni (Mark Manson Muswati Nyahada) was installed as Acting Chief on 9 September 2011.',
    era: 'Pre-colonial to present',
    coordinates: { x: 74, y: 35 },
  },
  {
    id: 'chief-chiduku',
    name: 'Chief Chiduku',
    alternateNames: ['Chiduke'],
    district: 'Makoni',
    province: 'Manicaland',
    historicalTerritory: 'Makoni District',
    clanLineage: 'Manyika lineage',
    naturalBoundaries: 'Rusape River; Makoni hills',
    historicalNotes: 'Chief Chiduku (Rivai Mbaimbai) was installed on 22 March 2000. The Makoni District has several chieftainships including Makoni, Chiduku, Chipunza, Chikore and Tandi. The area is known for its rich agricultural land and cultural heritage.',
    era: 'Pre-colonial to present',
    coordinates: { x: 76, y: 33 },
  },
  {
    id: 'chief-saunyama',
    name: 'Chief Saunyama',
    alternateNames: ['Nyanga area chief'],
    district: 'Nyanga',
    province: 'Manicaland',
    historicalTerritory: 'Nyanga highlands and terraced agricultural lands',
    clanLineage: 'Mheta (Python) totem — Manyika lineage',
    naturalBoundaries: 'Eastern Highlands escarpment; Nyangani Mountain (highest peak in Zimbabwe)',
    historicalNotes: 'The Nyanga highlands contain extensive ancient terracing systems, pit structures, and stone enclosures — evidence of a sophisticated pre-colonial agricultural civilization. Chief Saunyama (Ephraim V. Saunyama) was installed as Acting Chief on 9 May 2012.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 78, y: 22 },
  },
  {
    id: 'chief-tangwena',
    name: 'Chief Tangwena',
    alternateNames: ['Tawungwena'],
    district: 'Nyanga',
    province: 'Manicaland',
    historicalTerritory: 'Nyanga District — Gaeresi Ranch area',
    clanLineage: 'Humba/Makombe (Bushpig/Warthog) totem — Manyika lineage',
    naturalBoundaries: 'Eastern Highlands; Gaeresi River',
    historicalNotes: 'Chief Rekayi Tangwena became a symbol of resistance against colonial land dispossession in the 1960s–70s, refusing to leave his ancestral land. Chief Tangwena (Morris Tangwena) was installed on 27 June 2006.',
    era: 'Pre-colonial to present',
    coordinates: { x: 80, y: 20 },
  },
  {
    id: 'chief-katerera',
    name: 'Chief Katerera',
    alternateNames: ['Katerere'],
    district: 'Nyanga',
    province: 'Manicaland',
    historicalTerritory: 'Nyanga District',
    clanLineage: 'Manyika lineage',
    naturalBoundaries: 'Eastern Highlands; Nyanga hills',
    historicalNotes: 'Chief Katerera (Matambo Chifodya) was installed on 19 March 1993. The chieftainship is in Nyanga District, known for its ancient terracing systems and the highest peak in Zimbabwe, Mount Nyangani.',
    era: 'Pre-colonial to present',
    coordinates: { x: 78, y: 18 },
  },
  {
    id: 'chief-musikavanhu',
    name: 'Chief Musikavanhu',
    alternateNames: ['Chipinge area chief'],
    district: 'Chipinge',
    province: 'Manicaland',
    historicalTerritory: 'Chipinge highlands and Save River valley',
    clanLineage: 'Nzou (Elephant) totem — Ndau lineage',
    naturalBoundaries: 'Save River to the west; Mozambique border to the east',
    historicalNotes: 'The Ndau people of Chipinge have a distinct cultural identity within the broader Shona grouping, with historical connections to the Sofala coast trade networks. Chief Musikavanhu (Vusani Mutemebvi Musikavanhu) was installed on 2 June 2009.',
    era: 'Pre-colonial to present',
    coordinates: { x: 80, y: 48 },
  },
  {
    id: 'chief-mahenye',
    name: 'Chief Mahenye',
    district: 'Chipinge',
    province: 'Manicaland',
    historicalTerritory: 'Chipinge District — Save Valley',
    clanLineage: 'Ndau lineage',
    naturalBoundaries: 'Save River; Runde River',
    historicalNotes: 'Chief Mahenye (Thomas Jojo) was installed in Chipinge District. The chieftainship is in the Save Valley area, known for its wildlife and the Gonarezhou National Park nearby. The Ndau people have strong connections to the Mozambican coast.',
    era: 'Pre-colonial to present',
    coordinates: { x: 78, y: 50 },
  },
  {
    id: 'chief-mutambara',
    name: 'Chief Mutambara',
    district: 'Chimanimani',
    province: 'Manicaland',
    historicalTerritory: 'Chimanimani District',
    clanLineage: 'Manyika lineage',
    naturalBoundaries: 'Chimanimani Mountains; Haroni River',
    historicalNotes: 'Chief Mutambara (John Godfrey Mutambara) was installed on 2 November 2011. The Chimanimani District is known for its spectacular mountains and is home to the Chimanimani National Park. The area has strong Manyika cultural traditions.',
    era: 'Pre-colonial to present',
    coordinates: { x: 76, y: 44 },
  },
  {
    id: 'chief-ngorima',
    name: 'Chief Ngorima',
    district: 'Chimanimani',
    province: 'Manicaland',
    historicalTerritory: 'Chimanimani District',
    clanLineage: 'Manyika lineage',
    naturalBoundaries: 'Chimanimani Mountains; Haroni River',
    historicalNotes: 'Chief Ngorima (Aaron Hondo Ngorima) was installed as Acting Chief on 5 July 2010. The chieftainship is in the Chimanimani District, known for its rugged mountain terrain and the Chimanimani National Park.',
    era: 'Pre-colonial to present',
    coordinates: { x: 78, y: 42 },
  },
  {
    id: 'chief-nyashanu',
    name: 'Chief Nyashanu',
    district: 'Buhera',
    province: 'Manicaland',
    historicalTerritory: 'Buhera District',
    clanLineage: 'Shava Museyamwa (Eland) totem — Karanga lineage',
    naturalBoundaries: 'Buhera hills; Mupfure River',
    historicalNotes: 'Chief Nyashanu (Rugare Chemwi Kangenga) was installed on 8 January 1986. The Buhera District is the homeland of the Nyashanu people, from whom the sons Chiweshe, Hwata and Gutsa migrated northwards. The area has strong Karanga cultural traditions.',
    era: 'Pre-colonial to present',
    coordinates: { x: 70, y: 40 },
  },
  {
    id: 'chief-zimunya',
    name: 'Chief Zimunya',
    district: 'Mutare',
    province: 'Manicaland',
    historicalTerritory: 'Mutare District',
    clanLineage: 'Manyika lineage',
    naturalBoundaries: 'Mutare River; Eastern Highlands',
    historicalNotes: 'Chief Zimunya (Kiben Bvirindi) was installed as Acting Chief on 20 July 2009. The Mutare District is the gateway to the Eastern Highlands and has been an important trade route between Zimbabwe and Mozambique since pre-colonial times.',
    era: 'Pre-colonial to present',
    coordinates: { x: 76, y: 38 },
  },

  // ── MASVINGO ──────────────────────────────────────────────────────────────
  {
    id: 'chief-charumbira',
    name: 'Chief Charumbira',
    district: 'Masvingo',
    province: 'Masvingo',
    historicalTerritory: 'Masvingo District — near Great Zimbabwe',
    clanLineage: 'Karanga lineage — Rozvi descent',
    naturalBoundaries: 'Mutirikwi (Kyle) reservoir; Runde River',
    historicalNotes: 'Chief Charumbira (Fortune Zephania Charumbira) was installed on 24 May 2000 and later became President of the National Council of Chiefs. The chieftainship is near Great Zimbabwe, the ancient stone city that gave Zimbabwe its name.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 54, y: 52 },
  },
  {
    id: 'chief-gutu',
    name: 'Chief Gutu',
    district: 'Gutu',
    province: 'Masvingo',
    historicalTerritory: 'Gutu plateau',
    clanLineage: 'Shava (Eland) totem — Karanga lineage',
    naturalBoundaries: 'Runde River to the south; Mutirikwi (Kyle) reservoir to the west',
    historicalNotes: 'The Gutu chiefdom is one of the oldest Karanga chieftainships, with oral traditions linking it to the Great Zimbabwe civilization. Chief Gutu (Anos Kasirayi Masanganise) was installed on 2 April 2009.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 60, y: 52 },
  },
  {
    id: 'chief-chivi',
    name: 'Chief Chivi',
    alternateNames: ['Chibi'],
    district: 'Chivi',
    province: 'Masvingo',
    historicalTerritory: 'Chivi lowlands',
    clanLineage: 'Shumba (Lion) totem — Karanga lineage',
    naturalBoundaries: 'Runde River corridor; bounded by granite inselbergs',
    historicalNotes: 'The Chivi chiefdom occupies the transitional zone between the highveld and the lowveld, historically important for cattle herding and seasonal agriculture. Chief Chivi (Chimba Sundire) was installed on 7 June 2010.',
    era: 'Pre-colonial to present',
    coordinates: { x: 55, y: 58 },
  },
  {
    id: 'chief-bikita',
    name: 'Chief Bikita',
    district: 'Bikita',
    province: 'Masvingo',
    historicalTerritory: 'Bikita highlands',
    clanLineage: 'Nzou (Elephant) totem — Karanga lineage',
    naturalBoundaries: 'Mutirikwi River to the north; Runde River to the south',
    historicalNotes: 'The Bikita area is known for its lithium and rare mineral deposits, which have been mined since pre-colonial times for trade. Chief Mabika (Tafiranido Mabika) was installed on 7 April 1989.',
    era: 'Pre-colonial to present',
    coordinates: { x: 65, y: 56 },
  },
  {
    id: 'chief-mwenezi',
    name: 'Chief Mwenezi',
    district: 'Mwenezi',
    province: 'Masvingo',
    historicalTerritory: 'Mwenezi lowveld',
    clanLineage: 'Tembo (Elephant) totem — Karanga lineage',
    naturalBoundaries: 'Mwenezi River defines the territory; Limpopo River to the south',
    historicalNotes: 'The Mwenezi lowveld was historically part of the Rozvi Empire\'s southern frontier. Chief Mazetese (Finga Tapera) was installed on 23 July 1996. The area borders South Africa and Mozambique, making it a historically significant crossroads.',
    era: 'Rozvi Empire era to present',
    coordinates: { x: 58, y: 68 },
  },
  {
    id: 'chief-zaka',
    name: 'Chief Ndanga',
    alternateNames: ['Zaka area chief'],
    district: 'Zaka',
    province: 'Masvingo',
    historicalTerritory: 'Zaka District',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Runde River; Zaka hills',
    historicalNotes: 'Chief Ndanga (Simon Charinda) was installed on 8 March 2010. The Zaka District has several chieftainships with strong Karanga heritage. The area is known for its connection to the Great Zimbabwe civilization and the Rozvi state.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 62, y: 58 },
  },
  {
    id: 'chief-murinye',
    name: 'Chief Murinye',
    district: 'Masvingo',
    province: 'Masvingo',
    historicalTerritory: 'Masvingo District',
    clanLineage: 'Karanga lineage — Rozvi descent',
    naturalBoundaries: 'Mutirikwi (Kyle) reservoir; Runde River',
    historicalNotes: 'Chief Murinye (Ephias Munodawafa) was installed on 5 October 2009. The chieftainship is near Great Zimbabwe and has strong connections to the Rozvi state. The area is one of the most historically significant in Zimbabwe.',
    era: 'Great Zimbabwe era to present',
    coordinates: { x: 56, y: 54 },
  },
  {
    id: 'chief-chiredzi',
    name: 'Chief Sengwa',
    alternateNames: ['Chiredzi area chief'],
    district: 'Chiredzi',
    province: 'Masvingo',
    historicalTerritory: 'Chiredzi lowveld — Save Valley',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Save River; Runde River; Gonarezhou National Park',
    historicalNotes: 'Chief Sengwa (Lisimati Willie Makoti) was installed on 21 October 1976. The Chiredzi lowveld is home to the Gonarezhou National Park and the Save Valley Conservancy. The area has been inhabited since the Iron Age.',
    era: 'Pre-colonial to present',
    coordinates: { x: 64, y: 64 },
  },

  // ── MIDLANDS ──────────────────────────────────────────────────────────────
  {
    id: 'chief-gokwe',
    name: 'Chief Chireya',
    alternateNames: ['Gokwe area chief'],
    district: 'Gokwe North',
    province: 'Midlands',
    historicalTerritory: 'Gokwe basin and Sengwa River valley',
    clanLineage: 'VaShangwe lineage',
    naturalBoundaries: 'Sengwa River to the north; Lutope River to the south',
    historicalNotes: 'Chief Chireya (Henry Chidzivo) was installed on 9 February 2005. The Shangwe people of Gokwe have a distinct identity, historically living in the tsetse-fly belt which provided natural protection. Chief Chireya was regarded as paramount chief in Gokwe with six headmen under him.',
    era: 'Pre-colonial to present',
    coordinates: { x: 30, y: 40 },
  },
  {
    id: 'chief-chirumhanzu',
    name: 'Chief Chirumanzu',
    alternateNames: ['Chirumhanzu'],
    district: 'Chirumhanzu',
    province: 'Midlands',
    historicalTerritory: 'Chirumhanzu highlands',
    clanLineage: 'Shava (Eland) totem — Karanga lineage',
    naturalBoundaries: 'Sebakwe River to the north; Mtilikwe River to the south',
    historicalNotes: 'The Chirumhanzu chiefdom sits at the crossroads of Karanga and Ndebele spheres of influence. Chief Chirumanzu (Gerald Mudzengi) was installed on 4 March 2005. The area has strong connections to both the Rozvi state and the Ndebele Kingdom.',
    era: 'Pre-colonial to present',
    coordinates: { x: 42, y: 50 },
  },
  {
    id: 'chief-mberengwa',
    name: 'Chief Mberengwa',
    district: 'Mberengwa',
    province: 'Midlands',
    historicalTerritory: 'Mberengwa highlands and gold belt',
    clanLineage: 'Shumba (Lion) totem — Karanga lineage',
    naturalBoundaries: 'Ngezi River to the north; Mberengwa hills as natural landmarks',
    historicalNotes: 'The Mberengwa area sits on one of Zimbabwe\'s richest gold belts, historically mined by the Rozvi and later by the British South Africa Company. Chief Bankwe (Silinga Sibanda) was installed on 22 June 1998.',
    era: 'Rozvi Empire era to present',
    coordinates: { x: 36, y: 56 },
  },
  {
    id: 'chief-gweru',
    name: 'Chief Gambiza',
    alternateNames: ['Gweru area chief'],
    district: 'Gweru',
    province: 'Midlands',
    historicalTerritory: 'Gweru District',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Gweru River; Midlands plateau',
    historicalNotes: 'Chief Gambiza (Freddy Sami) was installed on 18 February 2002. The Gweru District is in the heart of the Midlands Province and has been an important area for both Karanga and Ndebele peoples. The area has significant gold and chrome deposits.',
    era: 'Pre-colonial to present',
    coordinates: { x: 38, y: 48 },
  },
  {
    id: 'chief-kwekwe',
    name: 'Chief Gobo',
    alternateNames: ['Kwekwe area chief'],
    district: 'Kwekwe',
    province: 'Midlands',
    historicalTerritory: 'Kwekwe District',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Kwekwe River; Midlands plateau',
    historicalNotes: 'Chief Gobo (Clever Machona) was installed on 27 February 2003. The Kwekwe District is known for its gold and steel industries. The area has been inhabited since the Iron Age and has connections to the Great Zimbabwe civilization.',
    era: 'Pre-colonial to present',
    coordinates: { x: 36, y: 46 },
  },
  {
    id: 'chief-shurugwi',
    name: 'Chief Banga',
    alternateNames: ['Shurugwi area chief'],
    district: 'Shurugwi',
    province: 'Midlands',
    historicalTerritory: 'Shurugwi District',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Shurugwi hills; Sebakwe River',
    historicalNotes: 'Chief Banga (Lameck Toendepi) was installed on 9 February 2001. The Shurugwi District is known for its chrome deposits and has been an important mining area since pre-colonial times. The area has strong Karanga cultural traditions.',
    era: 'Pre-colonial to present',
    coordinates: { x: 40, y: 52 },
  },
  {
    id: 'chief-zvishavane',
    name: 'Chief Mafala',
    alternateNames: ['Zvishavane area chief'],
    district: 'Zvishavane',
    province: 'Midlands',
    historicalTerritory: 'Zvishavane District',
    clanLineage: 'Karanga lineage',
    naturalBoundaries: 'Mtilikwe River; Zvishavane hills',
    historicalNotes: 'Chief Mafala (Jongilizwe Matshazi) was installed on 4 September 1990. The Zvishavane District is known for its asbestos and gold deposits. The area has connections to the Rozvi state and the Great Zimbabwe civilization.',
    era: 'Pre-colonial to present',
    coordinates: { x: 38, y: 54 },
  },
  {
    id: 'chief-gokwe-south',
    name: 'Chief Nemangwe',
    alternateNames: ['Gokwe South area chief'],
    district: 'Gokwe South',
    province: 'Midlands',
    historicalTerritory: 'Gokwe South District',
    clanLineage: 'VaShangwe lineage',
    naturalBoundaries: 'Sengwa River; Lutope River',
    historicalNotes: 'Chief Nemangwe (Elijah Musongo) was installed on 17 December 2001. The Gokwe South District is part of the Shangwe heartland. The area is known for its cotton farming and has been an important agricultural region since the colonial era.',
    era: 'Pre-colonial to present',
    coordinates: { x: 28, y: 44 },
  },

  // ── MATABELELAND NORTH ────────────────────────────────────────────────────
  {
    id: 'chief-binga',
    name: 'Chief Siachilaba',
    alternateNames: ['Binga area chief'],
    district: 'Binga',
    province: 'Matabeleland North',
    historicalTerritory: 'Zambezi Valley (Binga)',
    clanLineage: 'Tonga lineage — Baleya clan',
    naturalBoundaries: 'Zambezi River (Lake Kariba) to the north; escarpment to the south',
    historicalNotes: 'The Tonga people of Binga were displaced by the creation of Lake Kariba in 1958. Chief Siachilaba (Mackson Mudimba) was installed on 4 March 1983. Their traditional territories now lie beneath the lake, making this one of Zimbabwe\'s most significant forced displacement histories.',
    era: 'Pre-colonial to present',
    coordinates: { x: 18, y: 28 },
  },
  {
    id: 'chief-siabuwa',
    name: 'Chief Siabuwa',
    district: 'Binga',
    province: 'Matabeleland North',
    historicalTerritory: 'Binga District — Zambezi Valley',
    clanLineage: 'Tonga lineage',
    naturalBoundaries: 'Lake Kariba to the north; Binga escarpment to the south',
    historicalNotes: 'Chief Siabuwa (Edward Njaya) was installed on 20 September 1984. The Binga District has numerous Tonga chieftainships, all displaced by the Kariba Dam. The Tonga people have maintained their cultural identity despite displacement.',
    era: 'Pre-colonial to present',
    coordinates: { x: 16, y: 30 },
  },
  {
    id: 'chief-sinampande',
    name: 'Chief Sinampande',
    district: 'Binga',
    province: 'Matabeleland North',
    historicalTerritory: 'Binga District — Zambezi Valley',
    clanLineage: 'Tonga lineage',
    naturalBoundaries: 'Lake Kariba to the north; Binga escarpment',
    historicalNotes: 'Chief Sinampande (Siakupwanya G. Sinampande) was installed on 12 December 1982. One of the many Tonga chieftainships in Binga District displaced by the Kariba Dam. The Tonga people are known for their distinctive culture and the Kariba fishing tradition.',
    era: 'Pre-colonial to present',
    coordinates: { x: 14, y: 32 },
  },
  {
    id: 'chief-hwange',
    name: 'Chief Hwange',
    district: 'Hwange',
    province: 'Matabeleland North',
    historicalTerritory: 'Hwange coalfields and wildlife corridor',
    clanLineage: 'Nxumalo lineage — Ndebele/Rozvi mixed heritage',
    naturalBoundaries: 'Deka River to the north; Gwaai River to the east',
    historicalNotes: 'The Hwange chiefdom sits atop one of Africa\'s largest coal deposits. Chief Dingani (Boy Joseph Dingani) was installed on 22 September 1999. The area was historically a hunting ground for both Ndebele and Rozvi peoples and is now home to Hwange National Park.',
    era: 'Rozvi Empire era to present',
    coordinates: { x: 14, y: 38 },
  },
  {
    id: 'chief-lupane',
    name: 'Chief Mabigwa',
    alternateNames: ['Mabhikwa', 'Lupane area chief'],
    district: 'Lupane',
    province: 'Matabeleland North',
    historicalTerritory: 'Lupane communal lands',
    clanLineage: 'Ndebele lineage — Zansi clan (Khumalo)',
    naturalBoundaries: 'Gwaai River to the west; Shangani River to the south',
    historicalNotes: 'Lupane was a resettlement area for Ndebele communities displaced during colonial land alienation. Chief Mabigwa (Vusumuzi Nicodemus Khumalo) was installed on 19 September 2011. The area has strong ties to the Ndebele royal house.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 20, y: 46 },
  },
  {
    id: 'chief-menyeza',
    name: 'Chief Menyeza',
    alternateNames: ['Meneza'],
    district: 'Lupane',
    province: 'Matabeleland North',
    historicalTerritory: 'Lupane District',
    clanLineage: 'Ndebele lineage — Gumede clan',
    naturalBoundaries: 'Gwaai River; Shangani River',
    historicalNotes: 'Chief Menyeza (Johnson Gumede) was installed on 2 November 1989. The Lupane District has several Ndebele chieftainships with strong connections to the Ndebele Kingdom established by Mzilikazi in the 1830s.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 22, y: 44 },
  },
  {
    id: 'chief-nkayi',
    name: 'Chief Nkalakatha',
    alternateNames: ['Nkayi area chief'],
    district: 'Nkayi',
    province: 'Matabeleland North',
    historicalTerritory: 'Nkayi communal lands — Lujinga area',
    clanLineage: 'Ndebele lineage — Enhla clan (Ndiweni)',
    naturalBoundaries: 'Shangani River defines the southern boundary',
    historicalNotes: 'The Nkayi District was made up of five chieftainships: Nkalakata, Tshugulu, Myinga Dagamela, Madhliwa and Sokobokobo. Chief Nkalakatha (Gilford Ndiweni) was installed on 11 December 1986. The Shangani River was the site of the famous Battle of Shangani (1893).',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 24, y: 50 },
  },
  {
    id: 'chief-madhliwa',
    name: 'Chief Madhliwa',
    alternateNames: ['Mandliwa'],
    district: 'Nkayi',
    province: 'Matabeleland North',
    historicalTerritory: 'Nkayi District',
    clanLineage: 'Ndebele lineage — Khumalo clan',
    naturalBoundaries: 'Shangani River; Nkayi hills',
    historicalNotes: 'Chief Madhliwa (Timothy K. Khumalo) was installed on 21 November 1964. One of the five chieftainships in Nkayi District. The area is historically part of the Ndebele Kingdom\'s cattle country.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 26, y: 48 },
  },
  {
    id: 'chief-tsholotsho',
    name: 'Chief Mahlathini',
    alternateNames: ['Tsholotsho area chief'],
    district: 'Tsholotsho',
    province: 'Matabeleland North',
    historicalTerritory: 'Tsholotsho District — formerly Tjolotjo/Nyamandhlovu',
    clanLineage: 'Ndebele lineage — Jiyane clan',
    naturalBoundaries: 'Gwaai River; Tsholotsho plains',
    historicalNotes: 'Tsholotsho (formerly Tjolotjo) was known as Nyamandhlovu — "where elephants are" — before independence. Chief Mahlathini (Edward Nkalivema Jiyane) was installed on 2 November 2011. The district borders Botswana and has strong Ndebele cultural traditions.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 16, y: 44 },
  },
  {
    id: 'chief-magama',
    name: 'Chief Magama',
    district: 'Tsholotsho',
    province: 'Matabeleland North',
    historicalTerritory: 'Tsholotsho District',
    clanLineage: 'Ndebele lineage — Hadebe clan',
    naturalBoundaries: 'Gwaai River; Tsholotsho plains',
    historicalNotes: 'Chief Magama (Conrad Lucky Magama Hadebe) was installed on 28 August 2002. The Tsholotsho District has several Ndebele chieftainships. The area is known for its wildlife and the Hwange National Park nearby.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 18, y: 42 },
  },
  {
    id: 'chief-bubi',
    name: 'Chief Mtshane',
    alternateNames: ['Bubi area chief'],
    district: 'Bubi',
    province: 'Matabeleland North',
    historicalTerritory: 'Bubi District',
    clanLineage: 'Ndebele lineage — Khumalo clan',
    naturalBoundaries: 'Bubi River; Umguza River',
    historicalNotes: 'Chief Mtshane (Mtshane Khumalo) was installed on 2 November 1989. The Bubi District is in the heart of Matabeleland North and has strong Ndebele cultural traditions. The area was part of the Ndebele Kingdom\'s heartland.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 22, y: 52 },
  },
  {
    id: 'chief-umguza',
    name: 'Chief Deli',
    alternateNames: ['Umguza area chief'],
    district: 'Umguza',
    province: 'Matabeleland North',
    historicalTerritory: 'Umguza District',
    clanLineage: 'Ndebele lineage — Mabhena clan',
    naturalBoundaries: 'Umguza River; Bulawayo Metropolitan border',
    historicalNotes: 'Chief Deli (Asher Mabhena) was installed on 4 March 1996. The Umguza District surrounds Bulawayo Metropolitan Province and has strong Ndebele cultural traditions. The area was part of the Ndebele Kingdom\'s royal heartland.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 24, y: 54 },
  },

  // ── MATABELELAND SOUTH ────────────────────────────────────────────────────
  {
    id: 'chief-matobo',
    name: 'Chief Malaba',
    alternateNames: ['Matobo area chief'],
    district: 'Matobo',
    province: 'Matabeleland South',
    historicalTerritory: 'Matobo Hills',
    clanLineage: 'Rozvi lineage — Mambo clan (Ncube)',
    naturalBoundaries: 'Defined entirely by the Matobo Hills granite formations',
    historicalNotes: 'The Matobo Hills are sacred to the Rozvi and Ndebele peoples, containing the royal burial site of Mzilikazi (founder of the Ndebele Kingdom) and Cecil Rhodes. Chief Malaba (Christopher Ncube) was installed on 13 August 1988. The hills are a UNESCO World Heritage Site.',
    era: 'Rozvi Empire era to present',
    coordinates: { x: 24, y: 62 },
  },
  {
    id: 'chief-nyangazonke',
    name: 'Chief Nyangazonke',
    alternateNames: ['Mabuyana'],
    district: 'Matobo',
    province: 'Matabeleland South',
    historicalTerritory: 'Matobo District',
    clanLineage: 'Ndebele lineage — Ndiweni clan',
    naturalBoundaries: 'Matobo Hills; Mzingwane River',
    historicalNotes: 'Chief Nyangazonke (Vuyane Ndiweni) was installed on 11 December 2006. The Matobo District has several chieftainships with both Rozvi and Ndebele heritage. The Matobo Hills are home to ancient San rock paintings.',
    era: 'Rozvi Empire era to present',
    coordinates: { x: 26, y: 60 },
  },
  {
    id: 'chief-gwanda',
    name: 'Chief Masuku',
    alternateNames: ['Gwanda area chief'],
    district: 'Gwanda',
    province: 'Matabeleland South',
    historicalTerritory: 'Gwanda lowveld',
    clanLineage: 'Ndebele lineage — Masuku clan',
    naturalBoundaries: 'Mzingwane River to the north; Tuli River to the south',
    historicalNotes: 'The Gwanda area was historically part of the Ndebele Kingdom\'s southern frontier, bordering Botswana (then Bechuanaland). Chief Masuku (Malaki Masuku) was installed on 10 September 1998.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 28, y: 68 },
  },
  {
    id: 'chief-insiza',
    name: 'Chief Madhuna',
    alternateNames: ['Insiza area chief'],
    district: 'Insiza',
    province: 'Matabeleland South',
    historicalTerritory: 'Insiza District',
    clanLineage: 'Ndebele lineage — Madhuna clan',
    naturalBoundaries: 'Insiza River; Mzingwane River',
    historicalNotes: 'Chief Madhuna (Vezi Madhuna) was installed on 27 August 1974. The Insiza District is in the heart of Matabeleland South and has strong Ndebele cultural traditions. The area was part of the Ndebele Kingdom\'s cattle country.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 30, y: 64 },
  },
  {
    id: 'chief-bulilima',
    name: 'Chief Kandana',
    alternateNames: ['Bulilima area chief'],
    district: 'Bulilima',
    province: 'Matabeleland South',
    historicalTerritory: 'Bulilima District',
    clanLineage: 'Ndebele lineage — Magutshwa clan',
    naturalBoundaries: 'Ramaquabane River (Botswana border); Mzingwane River',
    historicalNotes: 'Chief Kandana (Michael Magutshwa) was installed on 26 April 2007. The Bulilima District borders Botswana and has strong Ndebele cultural traditions. The area was part of the Ndebele Kingdom\'s western frontier.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 20, y: 66 },
  },
  {
    id: 'chief-mangwe',
    name: 'Chief Hobodo',
    alternateNames: ['Mangwe area chief'],
    district: 'Mangwe',
    province: 'Matabeleland South',
    historicalTerritory: 'Mangwe District',
    clanLineage: 'Ndebele lineage — Ncube clan',
    naturalBoundaries: 'Ramaquabane River (Botswana border); Mangwe Pass',
    historicalNotes: 'Chief Hobodo (Simon Thela Ncube) was installed on 23 July 2007. The Mangwe District is named after the Mangwe Pass, historically an important route between Zimbabwe and Botswana. The area has strong Ndebele cultural traditions.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 18, y: 64 },
  },
  {
    id: 'chief-beitbridge',
    name: 'Chief Matibe',
    alternateNames: ['Tshitaudze', 'Beitbridge area chief'],
    district: 'Beitbridge',
    province: 'Matabeleland South',
    historicalTerritory: 'Limpopo River valley',
    clanLineage: 'Venda/Karanga mixed lineage — Mbedzi clan',
    naturalBoundaries: 'Limpopo River forms the southern boundary (Zimbabwe-South Africa border)',
    historicalNotes: 'The Beitbridge area sits at the confluence of Venda, Karanga, and Ndebele cultural spheres, historically a crossing point on the Limpopo River trade route. Chief Matibe (Elisha Mbedzi) was installed on 9 September 2011.',
    era: 'Pre-colonial to present',
    coordinates: { x: 32, y: 76 },
  },
  {
    id: 'chief-umzingwane',
    name: 'Chief Sigola',
    alternateNames: ['Umzingwane area chief'],
    district: 'Umzingwane',
    province: 'Matabeleland South',
    historicalTerritory: 'Umzingwane District',
    clanLineage: 'Ndebele lineage — Sigola clan',
    naturalBoundaries: 'Mzingwane River; Umzingwane hills',
    historicalNotes: 'Chief Sigola (Zephania N. Sigola) was installed on 31 August 1994. The Umzingwane District is close to Bulawayo and has strong Ndebele cultural traditions. The Mzingwane River is a significant natural boundary in the area.',
    era: 'Ndebele Kingdom era to present',
    coordinates: { x: 26, y: 66 },
  },
];

export const PROVINCES_BY_ID = Object.fromEntries(PROVINCES.map(p => [p.id, p]));
export const CHIEFDOMS_BY_DISTRICT = CHIEFDOMS.reduce((acc, c) => {
  if (!acc[c.district]) acc[c.district] = [];
  acc[c.district].push(c);
  return acc;
}, {} as Record<string, Chiefdom[]>);
